import {
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonList,
  IonRouterLink,
  IonRow,
  IonText,
} from "@ionic/react";
import { useMemo, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { $api } from "@/api/api";
import { paths } from "@/api/v1";
import {
  LogRegHeader,
  LogRegPageLayout,
} from "@/pages/login/components/LoginRegisterLayout";
import { BASE_PATH } from "@/utils/constants/api";
import { ROUTING } from "@/utils/constants/routing";
import { SHORT_TIMED_DELAY } from "@/utils/constants/time";
import { inputClassNames } from "@/utils/formTools/inputClassName";

import "./LogReg.css";

type Inputs =
  paths["/login/forgot-password"]["post"]["requestBody"]["content"]["application/json"];

function readUserNameFromSearch(search: string): string {
  return new URLSearchParams(search).get("userName")?.trim() ?? "";
}

function ForgotPasswordInput({
  register,
  errors,
  touchedFields,
  disabled,
}: Pick<UseFormReturn<Inputs>, "register"> &
  Pick<UseFormReturn<Inputs>["formState"], "errors" | "touchedFields"> & {
    disabled: boolean;
  }) {
  return (
    <IonList lines="none" className="content-block">
      <IonItem>
        <IonInput
          className={inputClassNames({ errors, touchedFields }, "userName")}
          disabled={disabled}
          errorText=" "
          placeholder="Username"
          {...register("userName", { required: true })}
        />
      </IonItem>
    </IonList>
  );
}

function useForgotPasswordForm(initialUserName: string) {
  const { mutate, isIdle, reset } = $api.useMutation(
    "post",
    "/login/forgot-password"
  ),
    {
      register,
      handleSubmit,
      watch,
      formState: { errors, touchedFields },
    } = useForm<Inputs>({
      defaultValues: { userName: initialUserName },
    }),
    userName = watch("userName"),
    [responseMessage, setResponseMessage] = useState<string | null>(null),
    [submitError, setSubmitError] = useState<string | null>(null);

  function onSubmit(data: Inputs) {
    setSubmitError(null);
    setResponseMessage(null);
    mutate(
      { baseUrl: BASE_PATH, body: { ...data } },
      {
        onError(error) {
          setResponseMessage(null);
          setSubmitError(
            error.reason ??
            "Something went wrong. Please try again or contact support."
          );
        },
        onSettled() {
          setTimeout(reset, SHORT_TIMED_DELAY);
        },
        onSuccess(res) {
          setSubmitError(null);
          setResponseMessage(res.message);
        },
      }
    );
  }

  return {
    canSubmit: Boolean(userName?.trim()),
    errors,
    formDisabled: Boolean(responseMessage),
    handleSubmit,
    isIdle,
    onSubmit,
    register,
    responseMessage,
    submitError,
    touchedFields,
  };
}

function ForgotPasswordForm({
  initialUserName,
}: {
  initialUserName: string;
}) {
  const {
    canSubmit,
    errors,
    formDisabled,
    handleSubmit,
    isIdle,
    onSubmit,
    register,
    responseMessage,
    submitError,
    touchedFields,
  } = useForgotPasswordForm(initialUserName);

  return (
    <LogRegPageLayout>
      <IonGrid>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <LogRegHeader
            title="Forgot Password"
            subHeader={
              <IonText>
                Enter your username and we will send a reset link to the email
                on that account if we can reach you.
              </IonText>
            }
          />
          {responseMessage ? (
            <IonRow className="ion-justify-content-center content-block">
              <IonCol>
                <IonText color="success">{responseMessage}</IonText>
              </IonCol>
            </IonRow>
          ) : (
            <ForgotPasswordInput
              disabled={formDisabled}
              {...{ errors, register, touchedFields }}
            />
          )}
          {submitError && !responseMessage ? (
            <IonRow className="ion-justify-content-center content-block">
              <IonCol>
                <IonText color="danger">{submitError}</IonText>
              </IonCol>
            </IonRow>
          ) : null}
          <IonRow>
            <IonCol>
              <IonButton
                disabled={!isIdle || formDisabled || !canSubmit}
                type="submit"
                className="grow primary"
              >
                Send Reset Link
              </IonButton>
            </IonCol>
          </IonRow>
          <IonRow className="ion-justify-content-center">
            <IonCol className="shrink">
              <IonRouterLink routerLink={ROUTING.LOGIN}>
                Back to Login
              </IonRouterLink>
            </IonCol>
          </IonRow>
        </form>
      </IonGrid>
    </LogRegPageLayout>
  );
}

function ForgotPasswordPage() {
  const { search } = useLocation(),
    initialUserName = useMemo(() => readUserNameFromSearch(search), [search]);

  return <ForgotPasswordForm initialUserName={initialUserName} />;
}

export { ForgotPasswordPage };
